<template>
  <div>
    <div v-if="showSearch">
      <b-errors :errors="errors" class="mb-4" />

      <b-card :title="title" class="mb-4">
        <div v-if="isLoggedIn && !showFind">
          <p class="text-sm text-secondary mb-3">
            {{ $t('components.customerSelector.useLoggedInUser') }}
          </p>

          <UButton
            block
            size="lg"
            class="mb-2"
            @click="selectCustomer($auth.user.value.selectionObject)"
          >
            {{ $t('components.customerSelector.continueAs', { name: $auth.user?.value?.displayName || 'logged in user' }) }}
          </UButton>

          <UButton
            v-if="allowNewRegistration && !isAppLayout"
            block
            size="lg"
            variant="soft"
            @click="showFind = true"
          >
            {{ $t('components.customerSelector.createNewAccount') }}
          </UButton>
        </div>


        <div v-if="!isLoggedIn && isAppLayout">
          <img src="/img/bookapp-logo.png" class="mb-4 h-10 rounded-md mx-auto border" />
          <p class="text-secondary text-sm mb-2 text-center font-semibold">
            {{ $t('components.customerSelector.bookapp.accountRequired') }}
          </p>
          <p class="text-secondary text-sm mb-8 text-center opacity-70">
            {{ $t('components.customerSelector.bookapp.accountRequiredText') }}
          </p>
          <UButton
            block
            size="lg"
            color="red"
            @click="emitFlutterEvent('request-account')"
          >
            {{ $t('components.customerSelector.bookapp.createAccount') }}
          </UButton>
        </div>

        <div v-if="(!isLoggedIn || showFind) && !isAppLayout">
          <p class="text-secondary text-sm mb-4">
            {{ $t('components.customerSelector.checkIfExists') }}
          </p>

          <form @submit.prevent="submitSearch">
            <b-input-group class="mb-2">
              <UInput v-model.trim="email" size="lg" variant="outline" :placeholder="$t('general.email')" />
            </b-input-group>

            <b-button class="w-full" variant="action" :loading="searchLoading" @click="submitSearch">
              {{ $t('components.customerSelector.findCreateAccount') }}
            </b-button>
          </form>
        </div>
      </b-card>
    </div>

    <div v-if="showFound">
      <b-card
        :title="title"
        :subtitle="$t('components.customerSelector.selectCustomer')"
        class="mb-3 "
      >
        <p v-if="allowNewRegistration" class="text-secondary text-sm mb-4">
          {{ $t('components.customerSelector.findNameOrCreate') }}
        </p>

        <p v-if="!allowNewRegistration" class="text-secondary text-sm mb-4">
          {{ $t('components.customerSelector.findName') }}
        </p>

        <div class="mb-3">
          <b-button
            v-for="customer in response.customers"
            :key="customer.id"
            variant="action"
            class="mb-1 w-full"
            @click="selectCustomer(customer)"
          >
            {{ customer.displayName }}
          </b-button>
        </div>

        <b-button v-if="allowNewRegistration" flat class="w-full" @click="newCustomer">
          {{ $t('components.customerSelector.createNewCustomer') }} »
        </b-button>
      </b-card>
    </div>

    <customer-create
      v-if="showNew"
      :title="title"
      :subtitle="$t('components.customerSelector.info')"
      :response="response"
      :newCustomerDataProp="newCustomerData"
      @newSearch="newSearch"
      @success="handleCustomerCreateSuccess"
    />

    <div v-if="showNoNewRegistrations">
      <b-alert
        :title="title"
        :sub-title="$t('components.customerSelector.selectCustomer')"
        class="mb-3"
      >
        <p class="card-text">
          {{ showNoNewRegistrationsMessage || $t('components.customerSelector.notAcceptingNewCustomers') }}
        </p>
      </b-alert>
    </div>

    <div v-if="showNoOnlineBooking">
      <b-alert
        :title="title"
        :sub-title="$t('components.customerSelector.selectCustomer')"
        class="mb-3"
      >
        <p class="card-text">
          {{ showNoOnlineBookingMessage || $t('components.customerSelector.notAcceptingNewCustomers') }}
        </p>
      </b-alert>
    </div>
  </div>
</template>

<script>
import CustomerCreate from './Create.vue'

import Utils from '@/utils'

import * as Fields from '@/components/fields'

export default {
  components: {
    CustomerCreate
  },
  
  props: {
    title: {
      type: String,
      required: false,
      default: 'Find Customer'
    },
    allowNewRegistration: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  data () {
    return {
      errors: [],
      email: '',
      response: null,
      foundCustomers: null,
      showSearch: true,
      showFind: false,
      showFound: false,
      showNew: false,
      showNoNewRegistrations: false,
      showNoNewRegistrationsMessage: null,
      showNoOnlineBooking: false,
      showNoOnlineBookingMessage: null,
      newCustomerData: {
        fields: {
          email: null
        },
        customFields: {},
        privacyConsentAccepted: false,
        marketingConsentAccepted: false
      },
      searchLoading: false,
      requireBookApp: false,
      eventListeners: []
    }
  },
  async fetch ({ store, params, route }) {
    await store.dispatch('booking/setFromUrlQuery', route.query)
  },
  computed: {
    isAppLayout () {
      return this.$store.getters.layout === 'app'
    },
    isLoggedIn () {
      return this.$auth.isLoggedIn?.value && this.$auth.user?.value
    }
  },
  async mounted () {
    if (!this.isLoggedIn && this.isAppLayout && window?.flutter_inappwebview?.callHandler) {
      const sendAccountListener = (event) => {
        const data = event.detail

        if (data.user) {
          const user = JSON.parse(data.user)

          for (const key in user) {
            if (this.newCustomerData.fields[key] === undefined || this.newCustomerData.fields[key] === '') {
              this.newCustomerData.fields[key] = user[key]
            }
          }
        }
      }

      const sendTokenListener = (event) => {
        const data = event.detail
        const token = data.token

        this.loginWithToken(token)
      }

      const requestAccountCancelListener = () => {
        // console.log('request-account-cancel')
      }

      this.handleFlutterEvent('send-account', sendAccountListener)
      this.handleFlutterEvent('send-token', sendTokenListener)
      this.handleFlutterEvent('request-account-cancel', requestAccountCancelListener)

      this.eventListeners = [
        { name: 'bookapp-send-account', listener: sendAccountListener },
        { name: 'bookapp-send-token', listener: sendTokenListener },
        { name: 'bookapp-request-account-cancel', listener: requestAccountCancelListener }
      ]

      this.emitFlutterEvent('request-account')
    }
  },
  beforeUnmount() {
    // Clean up event listeners
    this.eventListeners.forEach(({ name, listener }) => {
      window?.removeEventListener(name, listener)
    })
  },
  methods: {
    fieldColumns (field) {
      if (field.type === 'textarea' || field.field === 'address') {
        return 'col-span-2'
      } else {
        return ''
      }
    },
    async loginWithToken (token) {
      await this.$auth.setToken(token).then(() => {
        this.selectCustomer(this.$auth.user.selectionObject)
      })
    },
    selectCustomer (customer) {
      if (
        customer.allowOnlineBooking !== undefined &&
        !customer.allowOnlineBooking
      ) {
        this.noOnlineBooking(
          customer.errors && customer.errors.length > 0
            ? customer.errors[0]
            : null
        )
        return
      }

      this.$emit('selected', { customer })
    },
    noNewRegistrations (message) {
      this.showFound = false
      this.showSearch = false
      this.showNew = false
      this.showNoOnlineBooking = false
      this.showNoNewRegistrations = true
      this.showNoNewRegistrationsMessage = message
    },
    noOnlineBooking (message) {
      this.showFound = false
      this.showSearch = false
      this.showNew = false
      this.showNoOnlineBooking = true
      this.showNoOnlineBookingMessage = message
      this.showNoNewRegistrations = false
    },
    newCustomer () {
      this.showFound = false
      this.showSearch = false
      this.showNew = true
      this.showNoOnlineBooking = false
      this.showNoNewRegistrations = false

      this.newCustomerData.fields.email = this.email
    },
    newSearch () {
      this.showSearch = true
      this.showFound = false
      this.showNew = false
    },
    async submitSearch ({ requireEmail = true } = {}) {
      this.searchLoading = true

      this.errors = []

      if (requireEmail) {
        if (!this.email) {
          this.errors.push(this.$t('components.customerSelector.emailRequired'))
        } else if (!Utils.validation.email(this.email)) {
          this.errors.push(
            this.$t('components.customerSelector.validEmailRequired')
          )
        }
      }

      if (this.errors.length > 0) {
        this.searchLoading = false
        return false
      }

      const response = await this.$api.booking.customer(this.email)
      this.response = response

      if (response.found) {
        this.foundCustomers = response.customers
        this.showFound = true
        this.showSearch = false
        this.showNew = false
        this.showNoOnlineBooking = false
        this.showNoNewRegistrations = false
        if (!response.allowNewRegistration) {
          // this.allowNewRegistration = false
        }
      } else if (!this.allowNewRegistration) {
        this.noNewRegistrations()
      } else if (!response.allowNewRegistration) {
        this.noNewRegistrations(
          response.errors && response.errors.length > 0
            ? response.errors[0]
            : null
        )
      } else {
        this.newCustomer()
      }

      if (this.response.fields) {
        for (let i = 0; i < this.response.fields.length; i++) {
          const field = this.response.fields[i]

          if (field.type === 'text') {
            field.component = Fields.TextField
          } else if (field.type === 'textarea') {
            field.component = Fields.TextAreaField
          } else if (field.type === 'list') {
            field.component = Fields.ListField

            if (field.custom && field.customField) {
              const options = {}
              for (
                let j = 0;
                j < field.customField.multipleValues.length;
                j++
              ) {
                const option = field.customField.multipleValues[j]
                options[option] = option
              }

              field.componentProps = { options }
            } else {
              field.componentProps = { options: field.options }
            }
          } else if (field.type === 'checkbox') {
            field.component = Fields.ToggleField
          } else if (field.type === 'date') {
            field.component = Fields.DateField
            if (field.field === 'dateOfBirth') {
              field.componentProps = {
                augmenter: 'YMD'
              }
            } else {
              field.componentProps = {
                augmenter: 'timestamp'
              }
            }
          } else {
            field.component = Fields.TextField
          }

          if (field.field === 'email') {
            field.disabled = true
          }

          if (field.custom && field.customField) {
            if (
              this.newCustomerData.customFields[field.customField.id] ===
              undefined
            ) {
              /* Vue.set(
                this.newCustomerData.customFields,
                field.customField.id,
                field.customField.defaultValue
              ) */

              this.newCustomerData.customFields[field.customField.id] = field.customField.defaultValue
            }
          }
        }
      }

      this.searchLoading = false
    },
    emitFlutterEvent (name) {
      return window?.flutter_inappwebview?.callHandler(`bookapp-${name}`)
    },
    handleFlutterEvent (name, callback) {
      return window?.addEventListener(`bookapp-${name}`, callback, false)
    },
    handleCustomerCreateSuccess (customer) {
      this.selectCustomer(customer)
    }
  }
}
</script>
